import React, { useState, useEffect } from "react";
import { Crown, Trash2, History, UserMinus, Clock, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import api from "@/const/axios";

interface TeamMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamMembersTeam: any | null;
  teamMembersLoading: boolean;
  teamMembersData: any[];
  ownProfile: boolean;
  profileId?: number;
  onTransferCaptaincy: (teamId: number, userId: number) => void;
  onRemoveMember: (teamId: number, userId: number) => void;
  onDeleteTeam: (teamId: number, confirmName: string) => void;
}

type ActiveTab = "members" | "history";

const ACTION_LABELS: Record<string, string> = {
  player_added:    "Əlavə edildi",
  player_removed:  "Çıxarıldı",
  captain_changed: "Kapitan dəyişdi",
  team_deleted:    "Komanda silindi",
};

const ACTION_COLORS: Record<string, string> = {
  player_added:    "text-green-600",
  player_removed:  "text-red-500",
  captain_changed: "text-amber-500",
  team_deleted:    "text-gray-500 dark:text-gray-400",
};

const TeamMembersDialog: React.FC<TeamMembersDialogProps> = ({
  open,
  onOpenChange,
  teamMembersTeam,
  teamMembersLoading,
  teamMembersData,
  ownProfile,
  profileId,
  onTransferCaptaincy,
  onRemoveMember,
  onDeleteTeam,
}) => {
  const [tab, setTab] = useState<ActiveTab>("members");

  // History
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");

  const iAmCaptain = teamMembersTeam?.isCaptain && ownProfile;

  // Reset state when dialog closes / team changes
  useEffect(() => {
    if (!open) {
      setTab("members");
      setShowDeleteConfirm(false);
      setConfirmInput("");
      setHistory([]);
    }
  }, [open]);

  const loadHistory = async () => {
    if (!teamMembersTeam) return;
    setHistoryLoading(true);
    try {
      const res = await api.get(`teams/${teamMembersTeam.id}/history`);
      setHistory(res.data);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTabChange = (t: ActiveTab) => {
    setTab(t);
    if (t === "history" && history.length === 0) {
      loadHistory();
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
    setConfirmInput("");
  };

  const handleDeleteConfirm = () => {
    if (!teamMembersTeam) return;
    onDeleteTeam(teamMembersTeam.id, confirmInput);
    onOpenChange(false);
  };

  const nameMatches = confirmInput.trim().toLowerCase() === teamMembersTeam?.name?.trim().toLowerCase();

  const formatActor = (log: any) => {
    const a = log.actor;
    if (!a) return "Sistem";
    return `${a.name ?? ""} ${a.surname ?? ""}`.trim() || `#${a.id}`;
  };

  const formatTarget = (log: any) => {
    const t = log.target;
    if (!t) return "";
    return `${t.name ?? ""} ${t.surname ?? ""}`.trim() || `#${t.id}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {teamMembersTeam?.name}
            {iAmCaptain && <Crown className="w-3.5 h-3.5 text-amber-500" />}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl mb-1">
          {(["members", "history"] as ActiveTab[]).map((t) => (
            <button
              key={t}
              onClick={() => handleTabChange(t)}
              className={cn(
                "flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center justify-center gap-1",
                tab === t ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              )}
            >
              {t === "members" ? <><span>Üzvlər</span></> : <><History className="w-3 h-3" /><span>Tarixçə</span></>}
            </button>
          ))}
        </div>

        {/* ── Members tab ── */}
        {tab === "members" && (
          <>
            {teamMembersLoading ? (
              <div className="py-8 flex justify-center">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-1">
                {teamMembersData.map((tu: any) => {
                  const isMe = tu.user?.id === profileId;
                  const isMemberCaptain = tu.isCaptain === true;
                  return (
                    <div key={tu.id} className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800">
                      <div className="flex items-center gap-2 min-w-0">
                        {isMemberCaptain && <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                            {tu.user?.name} {tu.user?.surname}
                            {isMe && <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">(siz)</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Transfer captaincy */}
                        {iAmCaptain && !isMe && !isMemberCaptain && (
                          <button
                            onClick={() => onTransferCaptaincy(teamMembersTeam.id, tu.user?.id)}
                            className="text-xs font-semibold px-2 py-1 rounded-lg border border-amber-200 text-amber-600 hover:bg-amber-50 transition-colors"
                          >
                            Kapitan et
                          </button>
                        )}
                        {/* Remove member — captain can remove non-captain non-self */}
                        {iAmCaptain && !isMe && !isMemberCaptain && (
                          <button
                            onClick={() => onRemoveMember(teamMembersTeam.id, tu.user?.id)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-500 hover:bg-red-50 dark:bg-red-950 hover:text-red-500 transition-colors"
                            title="Komandadan çıxar"
                          >
                            <UserMinus className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Delete team — captain only */}
            {iAmCaptain && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                {!showDeleteConfirm ? (
                  <button
                    onClick={handleDeleteClick}
                    className="flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Komandanı sil
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-950 rounded-xl">
                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700 leading-relaxed">
                        Bu əməliyyat geri qaytarıla bilməz. Davam etmək üçün komanda adını (<strong>{teamMembersTeam?.name}</strong>) tam olaraq daxil edin.
                      </p>
                    </div>
                    <Input
                      placeholder={`"${teamMembersTeam?.name}" yazın`}
                      value={confirmInput}
                      onChange={(e) => setConfirmInput(e.target.value)}
                      className="text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => { setShowDeleteConfirm(false); setConfirmInput(""); }}
                      >
                        Ləğv et
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex-1"
                        disabled={!nameMatches}
                        onClick={handleDeleteConfirm}
                      >
                        Sil
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ── History tab ── */}
        {tab === "history" && (
          <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
            {historyLoading ? (
              <div className="py-8 flex justify-center">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">Tarixçə yoxdur.</p>
            ) : (
              history.map((log: any) => (
                <div key={log.id} className="flex gap-2.5 py-2 px-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800">
                  <div className="flex-shrink-0 mt-0.5">
                    <Clock className="w-3.5 h-3.5 text-gray-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs leading-snug text-gray-700 dark:text-gray-200">
                      <span className="font-semibold">{formatActor(log)}</span>
                      {" "}
                      <span className={cn("font-medium", ACTION_COLORS[log.action])}>
                        {ACTION_LABELS[log.action] ?? log.action}
                      </span>
                      {log.target && (
                        <> — <span className="font-semibold">{formatTarget(log)}</span></>
                      )}
                    </p>
                    {log.note && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{log.note}</p>}
                    <p className="text-[11px] text-gray-300 mt-0.5">
                      {new Date(log.occurredAt).toLocaleString("az-AZ")}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TeamMembersDialog;
